// src/components/LivePlayer.js
// Live stream player using Mux Player web component
// Tracks viewer count via Firestore (increment on join, decrement on leave)
// Shows branded "Thank you for joining" screen when class ends

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { joinLiveClass, leaveLiveClass } from '../services/liveClasses';
import { REACTION_EMOJIS, sendReactionTap, subscribeToReactionTaps } from '../services/classReactions';
import { recordLiveAttendance } from '../services/liveAttendance';
import { serverNow, syncServerClock } from '../services/serverClock';
import { subscribeCastSession } from '../services/castSession';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';

const MUX_PLAYER_CDN = 'https://cdn.jsdelivr.net/npm/@mux/mux-player@3/dist/mux-player.min.js';
const CAST_SENDER_CDN = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

// LivePlayer — ll-live + DVR capable Mux Player wrapper (ADR: adr-live-class-rebuild.md).
//
// Accepts both `status` (legacy) and `state` (post-rebuild); treats them interchangeably
// so parents can pass whichever. `state === 'ending'` is the drain-window signal and is
// treated like 'live' for continued playback, but gates the thank-you overlay on the
// Mux Player firing `ended` (sealed manifest) to guarantee no viewer cutoff.
function LivePlayer({ playbackId, title, status, state, scheduledAt, classId, onStreamEnded, isAdmin, classType = 'live', isFavorited = false, onToggleFavorite, userId, endingAt = null }) {
    // Normalize lifecycle to a single value. Prefer `state` (post-rebuild); fall back
    // to `status` (legacy). Treat 'ending' (drain window) as 'live' for all playback
    // logic below — viewers are still watching. Only the overlay-render condition
    // distinguishes between live/ending/ended.
    const lifecycle = state || status;
    const isDraining = lifecycle === 'ending';
    // Back-compat alias used below everywhere `status` is checked — now reflects the
    // unified lifecycle. During the drain window we treat things as 'live' so the
    // player doesn't unmount, reactions keep flowing, presence stays visible, etc.
    // eslint-disable-next-line no-param-reassign
    status = (lifecycle === 'ending') ? 'live' : lifecycle;
    // Pre-recorded classes use Mux on-demand playback during their scheduled window.
    // Hoisted to the top so that effects declared above the player-creation effect
    // (e.g. the graceful-end safety timer) can read it without hitting a TDZ error
    // when the dep array is evaluated during render. See feedback_tdz_in_useeffect.md.
    const isPrerecorded = classType === 'prerecorded';
    const playerContainerRef = useRef(null);
    const [playerLoaded, setPlayerLoaded] = useState(false);
    // playerState: 'loading' | 'playing' | 'waiting' | 'ended' | 'error' | 'stalled'
    // 'stalled' is distinct from 'ended' — it means the stream never actually played
    // (e.g. empty HLS manifest from a broken broadcaster). We deliberately keep this
    // separate so we never show the "Thank you for joining" overlay for a class the
    // viewer never actually saw.
    const [playerState, setPlayerState] = useState('loading');
    // Keep the screen awake while the class is actively playing so the OS
    // doesn't dim/sleep/evict the (often installed-to-homescreen) PWA mid-class
    // and force a session re-hydration → signed-out bounce. Best-effort no-op
    // on unsupported browsers. See utils/useScreenWakeLock.js.
    useScreenWakeLock(playerState === 'playing');
    // Bumped when we need to fully rebuild the mux-player element — either silently
    // (auto-reload when a manifest hasn't populated yet) or explicitly via the
    // "Try Again" button on the stall overlay. Included in the player effect's
    // deps so incrementing it tears down the current <mux-player> and recreates it.
    const [reloadKey, setReloadKey] = useState(0);
    const hasJoinedRef = useRef(false);
    // Tracks whether the mux-player has EVER fired `playing` during this session.
    // Empty/stale HLS manifests can cause Mux Player to fire `ended` without ever
    // firing `playing`. Without this guard, those false-ended events show the
    // "Thank you for joining!" overlay on a class that never began streaming.
    const hasPlayedEverRef = useRef(false);
    // Timestamp when the CURRENT mux-player element most recently fired `playing`.
    // Mux Player's live HLS stack occasionally emits a spurious `ended` event a few
    // seconds after first playback — typically a manifest refresh that transiently
    // shows no playable segments, or a segment boundary hiccup. A real live stream
    // does not end within seconds of starting. We use this to ignore `ended` events
    // in a short window after `playing`, calling `.play()` to keep the element
    // live rather than tearing down and silent-reloading (which would expose the
    // iOS fresh-element autoplay block, stranding the viewer on Connecting).
    const playingStartedAtRef = useRef(0);
    const IGNORE_ENDED_WINDOW_MS = 10000;
    // Count of silent auto-reload cycles this live session has used. Each cycle is
    // one full "retry the mux-player element from scratch". We cap at SILENT_RELOAD_CAP
    // so we don't loop forever on a genuinely dead stream — after the cap, the
    // visible stall UI appears as a real escape hatch. During normal Mux HLS
    // propagation (5-15s) this counter stays at 0 and nothing user-visible happens.
    const silentReloadsRef = useRef(0);
    const silentReloadTimerRef = useRef(null);
    // Tracks the live mux-player element so the "Tap for sound" overlay can flip
    // .muted = false on the user's gesture. Cleared when the element is destroyed.
    const playerElementRef = useRef(null);
    // Mobile browsers (iOS Safari in particular) REFUSE to autoplay an unmuted
    // <video> — the playback attempt silently fails and the element never fires
    // `playing`, so our Connecting overlay spins forever. The fix is to mount the
    // player MUTED (which iOS allows to autoplay), then present a clear "Tap for
    // sound" affordance the viewer can interact with. A single tap satisfies the
    // user-gesture requirement and unmutes the stream.
    //
    // unmutedRef persists the viewer's unmute PREFERENCE across player rebuilds
    // (reloadKey bumps, silent reloads, error retries). Every mount starts muted
    // regardless — iOS silently rejects unmuted autoplay on a freshly created
    // element, so we can't just mount un-muted on reload and assume the prior
    // gesture still counts. After `playing` fires on the rebuilt element, the
    // handler programmatically flips muted=false IF unmutedRef is set (browsers
    // permit changing .muted on an actively playing element without a fresh
    // gesture). If even that is rejected, we fall back to surfacing the
    // Tap-for-sound overlay again — never leave the viewer with silent audio
    // and no affordance, and never leave them stuck on Connecting.
    const unmutedRef = useRef(false);
    const [needsUnmute, setNeedsUnmute] = useState(false);
    // ── Viewer entry model ──
    // Viewers enter via the "Join Waiting Room" click on the Workouts tile — that's
    // the premium gesture of intent. Once they're in the waiting room, the experience
    // is fully automatic: countdown → status flips to live → player mounts muted
    // (iOS always permits muted autoplay) → Connecting overlay bridges HLS manifest
    // propagation → playing fires → "Tap for sound" overlay appears as the single
    // in-player gesture to unlock audio. This matches the web live-video standard
    // (Twitch, YouTube Live, Instagram Live on web) and replaces the prior "Tap to
    // Join Class" second-tap gate, which introduced a double-tap trap on cold
    // manifests where iOS's transient activation window expired mid-retry.
    //
    // Admin's Viewer Preview auto-mounts identically and relies on the same
    // muted-autoplay + Tap-for-sound safety net for admin-on-mobile edge cases.
    // Cap chosen to cover the longest realistic Mux HLS propagation (~3-4 minutes of
    // silent retry) before surfacing any visible UI. During an active live class the
    // admin is still broadcasting — the stream WILL appear — so we prefer to keep the
    // Connecting overlay up rather than flash a "Try Again" screen that the viewer
    // would just tap through anyway. 25 cycles × 8s = ~200s of seamless retry.
    const SILENT_RELOAD_CAP = 25;
    const SILENT_RELOAD_DELAY_MS = 8000;
    // Schedule a silent auto-reload: bumps reloadKey after a delay and keeps the
    // Connecting overlay in place. Once we exceed the cap, we let the stall UI show
    // so the viewer has an explicit path forward. Safe to call multiple times — it
    // coalesces via the timer ref.
    const scheduleSilentReload = useCallback(() => {
        if (silentReloadTimerRef.current) return; // already scheduled
        if (silentReloadsRef.current >= SILENT_RELOAD_CAP) {
            setPlayerState('stalled');
            return;
        }
        setPlayerState('loading');
        silentReloadTimerRef.current = setTimeout(() => {
            silentReloadTimerRef.current = null;
            silentReloadsRef.current += 1;
            setReloadKey((k) => k + 1);
        }, SILENT_RELOAD_DELAY_MS);
    }, []);

    // ── Graceful end state ("End Class" lets the class play to its natural end) ──
    // When admin clicks End Class, we want viewers to continue playing through their HLS
    // buffer + any remaining Mux segments (so they hear Danielle's goodbye) BEFORE the
    // thank-you overlay appears. Admin signals this via `endingAt` on the class doc, and
    // we promote the NEXT Mux `ended` event to authoritative (instead of silent-reloading
    // per pattern #41).
    //
    // Safety ceiling: a 25s timer (see effect below, `endingAt` + 25000ms) forces the
    // thank-you overlay even if Mux never fires `ended`. This is WELL past Phase 2
    // manifest seal (T+12s) + generous LL-HLS buffer depth (~10s), so it does NOT
    // truncate real content — it covers the window where the player has drained the
    // stream but sits on a frozen end-of-stream frame / poster / seek indicator while
    // we wait for `ended` to eventually fire. Earlier versions had no internal timer
    // and relied on the T+120s Firestore flip as the escape hatch, but that left a
    // multi-second visible "flash" of Mux Player's native end-of-stream UI. The
    // `waiting`-after-drain-margin promotion below hits even earlier when applicable.
    //
    // Pattern #41 contract preserved: Mux `ended` ALONE is still not authoritative. It
    // becomes authoritative only when paired with the explicit admin `endingAt` signal —
    // so a transient mid-broadcast `ended` (no endingAt set) still silent-reloads.
    const gracefullyEndingRef = useRef(false);
    // Once we've authoritatively promoted to 'ended' (graceful drain complete or safety
    // timer fired), lock that state. Mux Player in DVR mode can fire stray `playing`
    // events after ENDLIST if the engine briefly replays segments from the sealed
    // manifest — without this lock, those events would flip playerState back to 'playing'
    // and un-render the thank-you overlay. Locked state is reset on reloadKey bump and
    // status leaving 'live'/'ending'.
    const endedLockedRef = useRef(false);
    // Graceful-end visual-hide latch. Mux Player's end-of-stream "dance" (seek-back
    // ~1-1.5s + replay of final segment) fires a fresh `playing` event on the replay,
    // and the `playing` handler unconditionally restores opacity=1. That overrode the
    // 2.0s pre-hide set by the `timeupdate` handler and produced a visible ~1s flash of
    // replayed content right before the thank-you overlay. Solution: once the timeupdate
    // handler hides the player during graceful-end (the only place opacity=0 is set in
    // this mode), latch this ref true — the `playing` handler then keeps opacity at 0
    // instead of restoring. Audio continues through the dance unchanged (opacity is
    // video-only); `ended` fires moments later and the overlay renders cleanly.
    // Resets alongside endedLockedRef when status leaves live (fresh class = clean slate).
    const gracefulHideLatchedRef = useRef(false);
    // Graceful-hide ACTIVE state — when true, the thank-you overlay renders over the
    // (already invisible via pre-hide) player IMMEDIATELY, without waiting for Mux's
    // `ended` event. This is the "zero slate, ever" safety net: once the manifest is
    // finite-duration AND the viewer is within the pre-hide window, whatever Mux
    // Player might do next (dance, slate, frame dupe, replay) is covered by the
    // branded thank-you overlay. Audio continues playing underneath so the viewer
    // still hears Danielle's closing words. Set true from the timeupdate handler at
    // the same moment it sets opacity=0 + latches gracefulHideLatchedRef. Resets
    // alongside endedLockedRef/gracefulHideLatchedRef when status leaves live.
    const [gracefulHideActive, setGracefulHideActive] = useState(false);
    // Force a re-render when gracefullyEndingRef flips, so the overlay condition re-evaluates.
    const [, forceOverlayRerender] = useState(0);
    // Live-mirror refs for endingAt and lifecycle so the `ended` handler (captured in a
    // closure at player-rebuild time) can see FRESH prop values. Without these, a
    // Firestore propagation of endingAt/state that lands AFTER the current mux-player
    // element was built but BEFORE it fires `ended` would be invisible to the handler,
    // causing it to fall through to silent-reload against the already-sealed manifest —
    // which rebuilds the player and replays the last segment (~1-3s audio replay bug).
    const endingAtRef = useRef(endingAt);
    const lifecycleRef = useRef(lifecycle);
    useEffect(() => { endingAtRef.current = endingAt; }, [endingAt]);
    useEffect(() => { lifecycleRef.current = lifecycle; }, [lifecycle]);
    const enterGracefulEndingIfNeeded = useCallback(() => {
        if (gracefullyEndingRef.current) return; // already in graceful-end mode
        if (!endingAt) return; // no admin signal yet
        if (!hasPlayedEverRef.current) return; // viewer never saw the class — late joiner path
        gracefullyEndingRef.current = true;
        forceOverlayRerender((n) => n + 1);
    }, [endingAt]);
    // When endingAt flips from falsy → truthy (admin clicked End Class), enter graceful end.
    useEffect(() => {
        enterGracefulEndingIfNeeded();
    }, [endingAt, enterGracefulEndingIfNeeded]);
    // Escape hatch: when Firestore status flips to 'ended' (admin's delayed cleanup), clear
    // any lingering graceful-ending state so the overlay can render. Covers the edge case
    // where Mux never fires `ended` (dead manifest, network issue) — the admin status
    // change is the hard signal that time's up.
    useEffect(() => {
        if (status === 'ended' && gracefullyEndingRef.current) {
            gracefullyEndingRef.current = false;
            endedLockedRef.current = true;
            setPlayerState('ended');
        }
    }, [status]);

    // Graceful-end safety timer: force the thank-you overlay 25s after endingAt if we
    // haven't already transitioned via Mux `ended`. Hides the "stock image + flash"
    // window where Mux Player is showing its native frozen-end-of-stream UI after the
    // manifest has drained but before (or without) an `ended` event. 25s covers Phase 2
    // seal (12s) + LL-HLS buffer depth (~10s) + margin — any real content is long past.
    // Only arms for live (not pre-recorded, which has a natural `ended` event always).
    // Skips if the viewer never played (late joiners are covered by the status=ended
    // path at T+120s).
    useEffect(() => {
        if (!endingAt || isPrerecorded) return;
        const elapsed = Date.now() - endingAt;
        const remaining = Math.max(0, 25000 - elapsed);
        const t = setTimeout(() => {
            if (!hasPlayedEverRef.current) return;
            if (lifecycleRef.current !== 'ending' && lifecycleRef.current !== 'ended') return;
            // Only force if we haven't already transitioned (overlay not yet showing).
            // A setPlayerState with the same value is effectively a no-op in React,
            // so double-calling is safe — but this keeps the log honest.
            if (gracefullyEndingRef.current) {
                console.log('Graceful-end safety timer elapsed — forcing thank-you overlay');
                gracefullyEndingRef.current = false;
                endedLockedRef.current = true;
                setPlayerState('ended');
            }
        }, remaining);
        return () => clearTimeout(t);
    }, [endingAt, isPrerecorded]);

    // ══════════════════════════════════════════════════════════════════════
    //  AUTO-COMPLETION ATTENDANCE TRACKING (≥75% threshold)
    // ══════════════════════════════════════════════════════════════════════
    // Accumulates time spent with playerState === 'playing' while status === 'live'.
    // The mux-webhook reads liveAttendance docs after creating the recording and
    // calls markWorkoutComplete for users whose watchedSeconds / videoDurationSeconds
    // is ≥ 0.75. Friend role is included; admin is excluded by design.
    //
    // Two refs (no React state — we don't want re-renders for accumulation):
    //   - watchedSecondsRef: cumulative seconds of active watching across the
    //     class session. Persists across silent-reload / reloadKey bumps because
    //     it lives at the LivePlayer component scope, not inside createPlayer.
    //   - enteredPlayingAtRef: wall-clock ms when player most recently entered
    //     'playing'. Zero when not currently in a playing span.
    //
    // Lifecycle:
    //   - Accumulator effect transitions in/out of 'playing' and updates refs.
    //   - Heartbeat effect runs while status === 'live', writes the running
    //     total every 30s. Cleanup flushes one final value when status leaves
    //     'live' OR LivePlayer unmounts — this is what captures attendance for
    //     viewers who stay on the thank-you overlay through the drain window.
    //
    // No reset effect: same-classId remounting yields a fresh component instance
    // (Workouts.js conditional render); same-classId same-mount transitioning
    // live → ended → live is admin-restart territory, where resuming from the
    // prior total is correct (attendance is cumulative across the merged session).
    //
    // See docs/decisions/adr-auto-completion.md for full design.
    const watchedSecondsRef = useRef(0);
    const enteredPlayingAtRef = useRef(0);
    // True while a cast session (Chromecast/AirPlay) is active for this viewer.
    // When casting, the local player goes idle so the playerState accumulator
    // below would record near-zero — the cast effect further down accumulates
    // wall-clock time instead. This ref keeps the two paths mutually exclusive
    // (no double-count). See adr-cast-aware-watch-tracking.md.
    const castConnectedRef = useRef(false);

    // Accumulator: react to playerState transitions. Effect ordering note —
    // declared BEFORE the heartbeat so React's effect-cleanup order on status
    // change (cleanups in declaration order, then bodies) means the heartbeat's
    // final flush sees the freshest accumulated total.
    useEffect(() => {
        if (isAdmin) return;
        if (!userId || !classId) return;
        if (status !== 'live') return;
        // `&& !castConnectedRef.current`: during a cast session the cast effect
        // owns accumulation, so we must NOT also open a local playing-span (would
        // double-count if AirPlay keeps the local element 'playing'). When not
        // casting this is exactly today's condition (`|| false` ⇒ unchanged).
        if (playerState === 'playing' && !castConnectedRef.current) {
            // Stamp entry; subsequent transitions out will accumulate the delta.
            enteredPlayingAtRef.current = Date.now();
        } else if (enteredPlayingAtRef.current > 0) {
            // Exited 'playing' — accumulate. Defensive cap at 1h prevents
            // runaway accumulation on a stuck timer / clock skew (e.g. tab
            // suspension on iOS dropped a transition we'd otherwise see).
            const delta = (Date.now() - enteredPlayingAtRef.current) / 1000;
            if (delta > 0 && delta < 3600) {
                watchedSecondsRef.current += delta;
            }
            enteredPlayingAtRef.current = 0;
        }
    }, [playerState, status, isAdmin, userId, classId]);

    // Heartbeat + final flush. The cleanup function is the load-bearing piece
    // for users who stay on the thank-you overlay (LivePlayer doesn't unmount
    // when status flips to ending/ended — it stays mounted to render the
    // overlay). Cleanup runs when `status` dep changes, so leaving 'live'
    // triggers the final flush. Component unmount (user navigates away)
    // ALSO runs cleanup, covering the second exit path.
    useEffect(() => {
        if (isAdmin) return;
        if (!userId || !classId) return;
        if (status !== 'live') return;

        const flush = () => {
            // Read fresh refs at flush time (not at effect-creation time) so
            // the value is current — closure-captured refs always read live.
            let total = watchedSecondsRef.current;
            if (enteredPlayingAtRef.current > 0) {
                const delta = (Date.now() - enteredPlayingAtRef.current) / 1000;
                if (delta > 0 && delta < 3600) total += delta;
            }
            // Skip the write if nothing meaningful has accumulated yet — saves
            // a Firestore write for users who arrived but haven't actually
            // started watching.
            if (total < 1) return;
            recordLiveAttendance(userId, classId, total, {
                videoTitle: title || '',
                classScheduledAt: scheduledAt || null,
            });
        };

        const intervalId = setInterval(flush, 30000);
        return () => {
            clearInterval(intervalId);
            // Final flush. Best-effort; if the network is offline, the
            // Firestore SDK queues the write locally and flushes on reconnect.
            flush();
        };
    }, [status, isAdmin, userId, classId, title, scheduledAt]);
    // ══════════════════════════════════════════════════════════════════════

    // ── Cast-aware live attendance (MEASUREMENT-ONLY) ──
    // When the viewer casts to a TV, the local mux-player goes idle and the
    // playerState accumulator above stops — so without this, cast viewers (often
    // the MOST engaged) record near-zero watch time and never auto-complete.
    // While a cast session is active we accumulate WALL-CLOCK seconds into the
    // same `watchedSecondsRef` the heartbeat already flushes. We only OBSERVE the
    // cast session — never control playback. Additive + cast-gated: with no
    // session active this is a complete no-op (subscribeCastSession never fires
    // onChange), so the non-cast path is byte-identical to today. For LIVE there
    // is no explicit duration cap — accumulation is naturally bounded by the
    // `status === 'live'` window (the class ends → this effect tears down).
    // See docs/decisions/adr-cast-aware-watch-tracking.md.
    useEffect(() => {
        if (isAdmin) return;
        if (!userId || !classId) return;
        if (status !== 'live') return;

        let castTickId = null;
        let lastTickAt = 0;

        const foldCastElapsed = () => {
            if (lastTickAt <= 0) return;
            const now = Date.now();
            const delta = (now - lastTickAt) / 1000;
            lastTickAt = now;
            // Same defensive per-fold cap as the local accumulator. Folding every
            // 10s keeps deltas tiny, so the cap never trims real watch time.
            if (delta > 0 && delta < 3600) watchedSecondsRef.current += delta;
        };

        const stopCastTick = () => {
            if (castTickId) { clearInterval(castTickId); castTickId = null; }
            foldCastElapsed(); // capture the final partial interval
            lastTickAt = 0;
        };

        const onCastChange = (isConnected) => {
            if (isConnected) {
                if (castConnectedRef.current) return;
                castConnectedRef.current = true;
                // Close any open LOCAL playing-span so the two paths never
                // overlap (mutual exclusion — prevents double-count if AirPlay
                // keeps the local element 'playing' under cast).
                if (enteredPlayingAtRef.current > 0) {
                    const d = (Date.now() - enteredPlayingAtRef.current) / 1000;
                    if (d > 0 && d < 3600) watchedSecondsRef.current += d;
                    enteredPlayingAtRef.current = 0;
                }
                lastTickAt = Date.now();
                castTickId = setInterval(foldCastElapsed, 10000);
            } else {
                if (!castConnectedRef.current) return;
                castConnectedRef.current = false;
                stopCastTick();
            }
        };

        const getMediaEl = () => (playerElementRef.current && playerElementRef.current.media) || null;
        const unsubscribe = subscribeCastSession(getMediaEl, onCastChange);

        return () => {
            unsubscribe();
            stopCastTick();
            castConnectedRef.current = false;
        };
    }, [status, isAdmin, userId, classId]);
    // ══════════════════════════════════════════════════════════════════════

    // ── Live reactions state ──
    // Taps fan out via Firestore (liveClasses/{classId}/reactionEvents) so all
    // viewers + admin see the same spectacle. The subscription below drives
    // setFloatingEmojis for everyone — self-taps arrive instantly via Firestore's
    // local-write echo, so we don't need separate optimistic state.
    const [floatingEmojis, setFloatingEmojis] = useState([]);
    const emojiCounterRef = useRef(0);
    const lastTapTsRef = useRef(0);

    const handleReactionTap = useCallback((emojiObj) => {
        if (!userId || !classId) return;
        // Rate limit: max 2 taps/sec per user (client-side — prevents spam, not a security gate)
        const now = Date.now();
        if (now - lastTapTsRef.current < 500) return;
        lastTapTsRef.current = now;
        sendReactionTap(classId, userId, emojiObj.emoji);
    }, [userId, classId]);

    const removeFloatingEmoji = useCallback((id) => {
        setFloatingEmojis((prev) => prev.filter((f) => f.id !== id));
    }, []);

    // Subscribe to live reaction taps from the server — drives floating emojis
    // for everyone (admin + viewers). Rendered for both true-live and pre-recorded
    // during their scheduled window AND the drain window ('ending'), per the
    // shared-moment parity model (viewers are still watching during drain).
    useEffect(() => {
        if (status !== 'live' || !classId) return;
        const unsubscribe = subscribeToReactionTaps(classId, (event) => {
            emojiCounterRef.current += 1;
            const id = `${event.id}-${emojiCounterRef.current}`;
            setFloatingEmojis((prev) => {
                const limited = prev.length >= 8 ? prev.slice(1) : prev;
                return [...limited, { id, emoji: event.emoji }];
            });
        });
        return () => unsubscribe();
    }, [status, classId]);

    // Server-clock sync (premiere): kick off as early as possible so the join-seek
    // and drift correction use the authoritative clock. Coalesced + cached at the
    // module level; degrades to the local clock on failure (serverNow()===Date.now()).
    useEffect(() => {
        if (isPrerecorded) syncServerClock().catch(() => {});
    }, [isPrerecorded]);

    // Load Mux Player + Google Cast framework scripts
    useEffect(() => {
        // Load Cast sender (enables Chromecast button in Mux Player)
        if (!document.querySelector(`script[src*="cast_sender"]`)) {
            const castScript = document.createElement('script');
            castScript.src = CAST_SENDER_CDN;
            castScript.defer = true;
            document.head.appendChild(castScript);
        }
        // Load Mux Player
        if (customElements.get('mux-player')) { setPlayerLoaded(true); return; }
        const script = document.createElement('script');
        script.src = MUX_PLAYER_CDN;
        script.async = true;
        script.onload = () => setPlayerLoaded(true);
        script.onerror = () => setPlayerState('error');
        document.head.appendChild(script);
    }, []);

    // Track viewer count — join on mount when live, leave on unmount or status change.
    // Pre-recorded classes also count as "live moments" when playing during their scheduled window:
    // viewers watch together, share presence, and react — the authoring distinction (live vs pre-recorded)
    // is irrelevant to the communal experience.
    useEffect(() => {
        if (status !== 'live' || !classId || isAdmin) return;

        joinLiveClass(classId);
        hasJoinedRef.current = true;

        return () => {
            if (hasJoinedRef.current) {
                leaveLiveClass(classId);
                hasJoinedRef.current = false;
            }
        };
    }, [status, classId, isAdmin]);

    // Also leave if the page unloads (user closes tab/navigates away)
    useEffect(() => {
        const handleUnload = () => {
            if (hasJoinedRef.current && classId) {
                leaveLiveClass(classId);
                hasJoinedRef.current = false;
            }
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [classId]);

    // ── Mux HLS propagation warmup ──
    // When status flips scheduled→live, Mux needs ~5–10s to publish the HLS manifest.
    // Mounting the Mux Player element before the manifest is ready puts it into a "loading"
    // limbo that can't always self-recover via the retry logic. Instead, we delay the mount
    // by 8 seconds whenever WE witnessed the transition. During this window, the
    // "Connecting to live stream…" overlay is visible (same condition: status === 'live' and
    // playerState !== 'playing'), so the user sees alive feedback rather than a frozen screen.
    //
    // Users who arrive AFTER the class is already live (status starts as 'live') skip the
    // warmup — the manifest is already published; they should load immediately.
    //
    // Pre-recorded classes also skip warmup — the Mux asset is already processed and ready.
    const [warmingUp, setWarmingUp] = useState(false);
    const prevStatusRef = useRef(status);
    useEffect(() => {
        const prev = prevStatusRef.current;
        prevStatusRef.current = status;
        if (prev === 'scheduled' && status === 'live' && !isPrerecorded) {
            setWarmingUp(true);
            const t = setTimeout(() => setWarmingUp(false), 8000);
            return () => clearTimeout(t);
        }
    }, [status, isPrerecorded]);

    // Create and manage the mux-player element
    useEffect(() => {
        if (!playerLoaded || !playbackId || status !== 'live' || !playerContainerRef.current) return;
        // Admin still waits out `warmingUp` so their Viewer Preview doesn't hit an
        // empty manifest on the scheduled→live transition they just triggered.
        // Viewers have no warmup — they mount the player immediately when status
        // flips to live, and the branded Connecting overlay covers HLS manifest
        // propagation (~5-10s on cold starts). Silent-reload loop handles any
        // propagation longer than that without surfacing a visible error.
        if (isAdmin && warmingUp) return;

        // Fresh attempt — reset the "has-played-ever" guard and normalize state back
        // to `loading` so the connecting overlay shows again during the retry.
        hasPlayedEverRef.current = false;
        playingStartedAtRef.current = 0;
        setPlayerState('loading');
        // A silent-reload just started (or this is the first attempt). Any pending
        // reload timer belongs to the previous cycle — cancel it so we don't double-fire.
        if (silentReloadTimerRef.current) {
            clearTimeout(silentReloadTimerRef.current);
            silentReloadTimerRef.current = null;
        }

        let retryCount = 0;
        const maxRetries = 8;
        let retryTimer = null;
        let safetyTimer = null;
        let driftTimer = null;
        let cancelled = false;

        function createPlayer() {
            if (cancelled || !playerContainerRef.current) return;

            // Clear any existing player
            if (playerContainerRef.current.firstChild) {
                playerContainerRef.current.removeChild(playerContainerRef.current.firstChild);
            }
            // Clear any drift-correction interval from a prior createPlayer pass
            // (retry / rebuild) so we never leak an interval onto a detached element.
            if (driftTimer) { clearInterval(driftTimer); driftTimer = null; }

            const player = document.createElement('mux-player');
            player.setAttribute('playback-id', playbackId);
            // Pre-recorded uses on-demand playback (full controls, seekable).
            // Live uses `ll-live` (low-latency HLS) + DVR — Mux records the rolling
            // live manifest so viewers get a scrubber, Go Live button, and pause
            // (D-048, D-049). Late joiners can rewind to t=0 of the broadcast.
            player.setAttribute('stream-type', isPrerecorded ? 'on-demand' : 'll-live');
            // Brand Mux Player's chrome to match the site palette.
            player.style.setProperty('--media-primary-color', 'var(--mom-primary)');
            player.style.setProperty('--media-accent-color', 'var(--mom-primary-dark)');
            player.setAttribute('autoplay', '');
            player.setAttribute('playsinline', '');
            player.setAttribute('default-hidden-captions', '');
            // Lock playback during airing — two layers of defense:
            //
            //   Layer A (`nohotkeys` + CSS variables, below): `nohotkeys`
            //   disables ALL keyboard shortcuts (space/k for play-pause,
            //   arrows/j/l for seek). The CSS variable setProperty calls hide
            //   the play-button, seek-backward-button, seek-forward-button,
            //   and time-range (scrubber). Verified via Mux Player v3
            //   shadow-DOM CSS inspection: the outer custom properties
            //   `--play-button`, `--seek-backward-button`, `--seek-forward-
            //   button`, `--time-range` propagate via inheritance into the
            //   shadow DOM, where they're assigned to `--media-X-display`,
            //   hiding the corresponding controls in ALL chrome regions
            //   (top/center/bottom — covers the bottom control bar AND the
            //   mobile centered tap-overlay).
            //
            //   Layer B (event listeners further down in this createPlayer):
            //   pause-revert and seek-revert. Catches any user-initiated
            //   pause or seek that slips past Layer A (e.g. mobile double-
            //   tap-to-seek if a future Mux Player release exposes it, or
            //   any remaining gesture surface we haven't anticipated).
            //
            // Combined, the layers make the player feel like a class —
            // playing through, no pause/scrub. Time display, duration
            // display, mute, volume, captions, fullscreen, AirPlay, and
            // Cast all stay interactive.
            player.setAttribute('nohotkeys', '');
            // Hide the four interactive controls. CSS variable values of
            // `none` cascade into Mux Player's shadow DOM and set the
            // matching `--media-X-display` to `none`, removing the elements.
            player.style.setProperty('--play-button', 'none');
            player.style.setProperty('--seek-backward-button', 'none');
            player.style.setProperty('--seek-forward-button', 'none');
            player.style.setProperty('--time-range', 'none');
            // Disable Mux Player's default poster image. Without this, mux-player
            // fetches `https://image.mux.com/{playbackId}/thumbnail.jpg` — for fresh
            // live streams with no captured thumbnail frame yet, Mux serves a generic
            // stock placeholder (workspace/coffee/mouse image) that leaks through
            // during brief frames between mount and first-play, mid-stream waiting
            // events, and DVR seek transitions. An empty poster means Mux Player
            // has no fallback image to render — only our branded overlays show.
            // 1x1 transparent PNG data URI (stronger signal than empty string —
            // some mux-player versions treat "" as "use default").
            player.setAttribute('poster', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=');
            // ALWAYS mount muted during the live window — iOS Safari silently blocks
            // unmuted autoplay on a freshly created media element unless there's a
            // recent user gesture in the current execution context. Silent reloads
            // (reloadKey bumps) and error-retry rebuilds happen far outside any
            // gesture window, so even if the viewer already tapped Tap-for-sound
            // a few seconds ago, remounting unmuted gets rejected → no `playing`
            // event → stuck on Connecting until a page refresh.
            //
            // Instead we mount muted every time and the `playing` handler below
            // re-applies the viewer's prior unmute choice programmatically (which
            // browsers permit while an element is actively playing). If that also
            // fails — e.g. Safari in a stricter state — we fall back to the
            // Tap-for-sound overlay, which is always safer than a dead stream.
            if (status === 'live') {
                player.setAttribute('muted', '');
                player.muted = true;
            }
            // needsUnmute is decided in the `playing` handler below so we don't
            // flash the overlay during the Connecting/loading phase.
            setNeedsUnmute(false);
            // DVR lets viewers rewind — don't disable seeking for ll-live.
            // Forward-seek and backward-seek offsets stay at Mux Player defaults.

            player.style.width = '100%';
            player.style.height = '100%';
            player.style.position = 'absolute';
            player.style.top = '0';
            player.style.left = '0';
            player.style.zIndex = '1';
            player.style.borderRadius = '20px';
            player.style.setProperty('--media-object-fit', 'cover');
            // Hide the player until it actually starts playing — shows our branded bg instead
            player.style.opacity = '0';
            player.style.transition = 'opacity 0.5s';

            player.addEventListener('playing', () => {
                // Guard: if we've already locked 'ended' (graceful drain completed or
                // safety timer fired), a subsequent `playing` event from Mux Player's
                // DVR engine briefly replaying segments after ENDLIST must NOT flip
                // us back to 'playing' — that would un-render the thank-you overlay
                // and expose the player frame. Once the class is over, it stays over.
                if (endedLockedRef.current) {
                    // Also visually hide the player element in case DVR is trying to
                    // render a replay behind our overlay.
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                    return;
                }
                hasPlayedEverRef.current = true;
                playingStartedAtRef.current = Date.now();
                setPlayerState('playing');
                // Graceful-end dance guard: if timeupdate has already hidden the
                // player because we're within 2.0s of a sealed manifest's end,
                // the `playing` event currently firing is Mux's end-of-stream
                // seek-back replay — do NOT un-hide. Without this check, the
                // video flashes visibly for ~200-300ms before the next
                // timeupdate re-hides, which reads as an accidental replay of
                // the instructor's closing words. Audio is unaffected; only
                // visibility is held.
                if (gracefulHideLatchedRef.current) {
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                } else {
                    player.style.opacity = '1';
                }
                if (retryTimer) clearTimeout(retryTimer);
                // If admin already clicked End Class before this viewer's first `playing`
                // event (late joiner during the graceful window), enter graceful-end mode
                // now so their buffer drains into the thank-you overlay at the right time.
                enterGracefulEndingIfNeeded();
                // Audio handling for live:
                //   - If viewer had previously tapped-for-sound this session (unmutedRef
                //     is true), try to programmatically restore their preference. This
                //     generally works because the element is already actively playing
                //     (muted autoplay succeeded) and browsers permit changing `.muted`
                //     on a playing element without a fresh gesture. If it doesn't work
                //     (verified by re-reading player.muted), we fall back to the
                //     Tap-for-sound overlay rather than leave them stuck with silent
                //     audio and no affordance.
                //   - If this is their first play of the session, show the Tap-for-sound
                //     overlay — this is the single in-player gesture (pattern #45, #47).
                if (status === 'live') {
                    if (unmutedRef.current) {
                        try {
                            player.muted = false;
                            if (player.volume === 0) player.volume = 1;
                            player.removeAttribute('muted');
                        } catch (_) { /* defensive — mux-player property sets shouldn't throw */ }
                        if (player.muted) {
                            // Auto-unmute was rejected — reset the ref and surface the
                            // overlay so the viewer can re-grant audio permission.
                            unmutedRef.current = false;
                            setNeedsUnmute(true);
                        }
                        // else: silent success, overlay stays hidden
                    } else {
                        setNeedsUnmute(true);
                    }
                }
            });
            player.addEventListener('waiting', () => {
                // Guard: if ended is locked, don't flip UI state to waiting — the
                // overlay must remain up through any post-ENDLIST DVR fidgeting.
                if (endedLockedRef.current) {
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                    return;
                }
                // Hide the mux-player element during mid-stream buffering so its
                // internal loading UI / any lingering poster frame can't leak through
                // the "Starting class…" overlay. The overlay (zIndex 3) covers the
                // player (zIndex 1), but iOS/DVR transitions have been observed to
                // render frames outside the normal stacking context — flipping
                // opacity to 0 is defense-in-depth. On the next `playing` event the
                // handler flips it back to 1.
                try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                setPlayerState('waiting');
                // Drain-margin promotion: once we're in graceful-end mode AND enough
                // time has elapsed past endingAt (Phase 2 seal at T+12s + LL-HLS buffer
                // depth ~3s = 15s margin), any `waiting` event means the player has
                // consumed its buffer and is stuck waiting for segments that will never
                // come. Promote to authoritative end now rather than letting the viewer
                // stare at Mux Player's frozen-end-of-stream UI until `ended` finally
                // fires (or until the 25s safety timer trips). Only fires for live —
                // pre-recorded always gets a legitimate `ended` at natural completion.
                // hasPlayedEverRef guard: a `waiting` event BEFORE first play would be
                // a cold-start buffering event, not end-of-drain.
                if (!isPrerecorded && hasPlayedEverRef.current &&
                    endingAtRef.current && (Date.now() - endingAtRef.current) >= 15000) {
                    console.log('Drain waiting: player hit end-of-buffer after drain margin — treating as class end');
                    gracefullyEndingRef.current = false;
                    endedLockedRef.current = true;
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                    setPlayerState('ended');
                    onStreamEnded?.();
                }
            });
            // ── End-of-stream visual pre-hide + thank-you cover (zero-slate guarantee) ──
            // Mux Player's LL-HLS engine does a brief "end-of-stream dance" right
            // before firing `ended` on a sealed manifest — a seek to a live-edge
            // that no longer exists, a frame duplicate at the boundary, or a
            // split-second waiting tick as the last segment drains. On top of
            // that, Mux itself may insert "slate" frames at the tail of the
            // sealed manifest under some broadcaster-disconnect scenarios. Both
            // categories of artifact would flash visibly between the last real
            // frame and the thank-you overlay unless we cover them.
            //
            // This handler does TWO things at the same moment:
            //   1. Hides the video element (opacity → 0) 2.0s before the sealed
            //      manifest's known end. Audio continues unaffected.
            //   2. Sets `gracefulHideActive` so React re-renders the thank-you
            //      overlay immediately (without waiting for Mux `ended`). The
            //      branded overlay covers whatever Mux is doing under it —
            //      slate, dance, replay, frozen frame, anything.
            //
            // The audio keeps playing, the viewer hears Danielle's closing
            // words, and the "Thank you for joining!" card is already in place
            // when the final audio fades. This is the industry-standard
            // end-transition pattern (Peloton / Obé / Apple Fitness+).
            //
            // Safety — this path CANNOT fire spuriously:
            //   - hasPlayedEverRef.current: the viewer was actually watching.
            //     A late joiner who never played (empty manifest, failed mount)
            //     never triggers this, so they don't get an overlay flashed at
            //     them for a class they never saw.
            //   - Number.isFinite(duration): on a LIVE stream, duration is
            //     Infinity UNTIL Mux's /complete seals the manifest and the
            //     player consumes `#EXT-X-ENDLIST`. Finite duration is proof
            //     that the stream has been authoritatively ended server-side.
            //     No amount of transient manifest hiccup can produce finite
            //     duration mid-broadcast — the gate is load-bearing.
            //   - dur - cur < 2.0: only within the final 2 seconds of the
            //     known end. Mux's observed end-of-stream seek-back is ~1–1.5s,
            //     so 2.0s gives a comfortable margin.
            //
            // Prior versions of this handler gated on `gracefullyEndingRef.current`
            // (set from the Firestore `endingAt` subscription). That gate lost a
            // race on cold networks where the HLS ENDLIST tag propagated to the
            // player BEFORE the Firestore `onSnapshot` fired on the viewer — so
            // pre-hide never activated and the dance rendered visibly. The
            // finite-duration guard is strictly stronger and entirely local to
            // the player, so it's immune to that race. Audio is unaffected —
            // opacity changes video visibility only, the media element's audio
            // track plays through unchanged.
            player.addEventListener('timeupdate', () => {
                if (endedLockedRef.current) return;
                if (!hasPlayedEverRef.current) return;
                const dur = player.media?.duration;
                const cur = player.media?.currentTime;
                if (!Number.isFinite(dur) || !Number.isFinite(cur)) return;
                if (dur - cur < 2.0) {
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                    // Latch the hide so Mux's end-of-stream dance (seek-back +
                    // replay → fresh `playing` event) cannot re-show the video
                    // via the opacity restore in the `playing` handler.
                    gracefulHideLatchedRef.current = true;
                    // Activate the thank-you overlay IMMEDIATELY — covers any
                    // slate/dance/replay that Mux might render in the remaining
                    // ~2s before the `ended` event promotes us to the locked
                    // ended state. State set (not ref) to force re-render.
                    setGracefulHideActive(true);
                }
            });
            player.addEventListener('ended', () => {
                // Graceful-end path: admin has signaled End Class (endingAt set) and this
                // viewer was actively playing. The stream is genuinely drained — promote
                // `ended` to authoritative. This is the ONLY path that promotes a live
                // `ended` event to a class end; without the admin signal, pattern #41
                // still applies and we silent-reload.
                if (gracefullyEndingRef.current) {
                    console.log('Graceful end: Mux `ended` fired after endingAt — treating as authoritative class end');
                    gracefullyEndingRef.current = false;
                    endedLockedRef.current = true;
                    try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                    setPlayerState('ended');
                    onStreamEnded?.();
                    return;
                }
                // For LIVE streams, the Mux player's `ended` event is NEVER a class end —
                // the class only ends when admin explicitly flips Firestore `status` to
                // 'ended'. A mid-broadcast `ended` is a manifest gap, a segment boundary
                // issue, or a brief broadcaster hiccup — the stream keeps coming.
                //
                // Two-tier response:
                //   1. If `ended` fires within IGNORE_ENDED_WINDOW_MS of our most recent
                //      `playing` event, it's almost certainly a transient glitch (manifest
                //      refresh, segment boundary). Real streams don't end seconds after
                //      starting. Ignore the event and immediately call .play() — the
                //      browser will seek back to the live edge as more segments arrive.
                //      This avoids the teardown/rebuild cycle that otherwise exposed the
                //      iOS fresh-element autoplay block (stuck on Connecting until
                //      refresh).
                //   2. Outside that window, silent-reload as before. The reload rebuilds
                //      the mux-player from scratch, and the rebuild mounts muted with the
                //      `playing` handler re-applying the viewer's unmute preference.
                if (!isPrerecorded) {
                    // ── Sealed-manifest authoritative detection (industry-standard HLS) ──
                    // HLS contract: `duration === Infinity` while the manifest is live,
                    // becomes a FINITE number the instant the player consumes the
                    // `#EXT-X-ENDLIST` tag written by Mux's action=complete (D-050
                    // Phase 2). If duration is finite, the manifest has been sealed
                    // server-side and this `ended` event is authoritative, regardless
                    // of whether `endingAt`/`state` props have propagated to this
                    // player instance yet. Without this check, a race where the
                    // Firestore prop arrives AFTER Mux has already sealed the manifest
                    // falls through to `scheduleSilentReload()` — which rebuilds the
                    // mux-player against the sealed stream and replays the last ~3s
                    // of buffered content (the "audio replay" bug).
                    //
                    // Safety: this check is purely additive and CANNOT cut the stream
                    // short. `duration` only becomes finite AFTER the player has
                    // actually read the ENDLIST marker — which by HLS definition sits
                    // at the true end of the recorded stream. Any transient mid-stream
                    // `ended` event (manifest gap, segment-boundary glitch, broadcaster
                    // dropout) leaves `duration === Infinity` and falls through to the
                    // existing race-safety / IGNORE_ENDED_WINDOW / silent-reload paths.
                    // This is the same technique used by HLS.js, video.js, and Shaka.
                    const muxDuration = player.media?.duration;
                    if (hasPlayedEverRef.current && Number.isFinite(muxDuration)) {
                        console.log(`Live \`ended\` with finite manifest duration (${muxDuration.toFixed(1)}s) — ENDLIST consumed, authoritative class end (no silent-reload replay)`);
                        gracefullyEndingRef.current = false;
                        endedLockedRef.current = true;
                        try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                        setPlayerState('ended');
                        onStreamEnded?.();
                        return;
                    }
                    // ── Race-safety fallback (pattern #41 still honored) ──
                    // `gracefullyEndingRef` above covers the "clean" case where endingAt had
                    // already propagated when the player was built. If endingAt / lifecycle
                    // landed between player-build and this ended-fire, the handler's
                    // closure captured stale prop values but the refs below are live —
                    // consult them directly. An admin end-signal paired with a played-ever
                    // viewer is authoritative, same contract as gracefullyEndingRef.
                    // Without this fallback, silent-reload would rebuild the player
                    // against an already-sealed manifest, causing a ~1-3s audio replay.
                    if (hasPlayedEverRef.current && (
                        !!endingAtRef.current
                        || lifecycleRef.current === 'ending'
                        || lifecycleRef.current === 'ended'
                    )) {
                        console.log('Live `ended` with admin end signal via refs — treating as authoritative class end (skipping silent reload against sealed manifest)');
                        gracefullyEndingRef.current = false;
                        endedLockedRef.current = true;
                        try { player.style.opacity = '0'; } catch (_) { /* noop */ }
                        setPlayerState('ended');
                        onStreamEnded?.();
                        return;
                    }
                    const timeSincePlaying = playingStartedAtRef.current
                        ? Date.now() - playingStartedAtRef.current
                        : Infinity;
                    if (timeSincePlaying < IGNORE_ENDED_WINDOW_MS) {
                        console.log(`Live fired \`ended\` ${timeSincePlaying}ms after playing — ignoring transient glitch, resuming playback`);
                        try { player.play().catch(() => { /* async play rejection is fine; silent reload path will catch it */ }); } catch (_) { /* sync rejection — ignore */ }
                        return;
                    }
                    console.log('Live stream fired `ended` while status=live — silent reload (not a class end)');
                    scheduleSilentReload();
                    return;
                }
                // Pre-recorded: `ended` is the legitimate end of the on-demand asset.
                // Still guard against false-ended when we never played (empty manifest).
                if (!hasPlayedEverRef.current) {
                    console.log('Pre-recorded fired `ended` before ever playing — scheduling silent reload');
                    scheduleSilentReload();
                    return;
                }
                setPlayerState('ended');
                onStreamEnded?.();
            });
            player.addEventListener('error', () => {
                console.log(`Mux Player error (attempt ${retryCount + 1}/${maxRetries}) — will retry`);
                if (retryCount < maxRetries && !cancelled) {
                    retryCount++;
                    // Progressive backoff: 3s, 5s, 7s, 10s...
                    const delay = Math.min(3000 + (retryCount * 2000), 15000);
                    if (safetyTimer) clearTimeout(safetyTimer);
                    retryTimer = setTimeout(() => createPlayer(), delay);
                } else if (!cancelled && !hasPlayedEverRef.current) {
                    // Exhausted internal retries and never played — schedule a silent
                    // reload that rebuilds the mux-player from scratch (new retry pool).
                    // User stays on the Connecting overlay; no visible error.
                    console.log('Mux Player exhausted retries without ever playing — scheduling silent reload');
                    scheduleSilentReload();
                }
            });

            // ── Playback lock (item 1): defense-in-depth pause + seek revert ──
            // Layer A (nohotkeys + CSS variables, set above) hides the play
            // button, scrubber, seek-backward/forward buttons and disables
            // keyboard shortcuts. Layer B (these event listeners) catches any
            // remaining vector — e.g. mobile double-tap-to-seek if a future
            // Mux Player release exposes it, or programmatic pause from
            // within the chrome we couldn't hide. Combined the layers make
            // the player play through like a class should.
            //
            // CRITICAL: every listener early-returns if `endedLockedRef.current` or
            // `gracefulHideLatchedRef.current` are true. Those refs flip TRUE during
            // graceful-end's "seek-back dance" (Mux's internal seek-back to a known
            // segment before firing the final `ended` event). Without these guards,
            // our revert would fight Mux's own machinery and break the zero-slate
            // end-of-stream guarantee. We also early-return when status leaves the
            // live/ending window — once status is 'ended' the overlay covers the
            // player and locks become moot.
            //
            // Closure-scoped (not a ref) because each player instance gets its own
            // listeners and the value resets when the player is rebuilt on reloadKey
            // bumps. `lastValidTime` tracks the most recent currentTime where the
            // player was actively playing — the snap-back target on a user seek.
            let lastValidTime = 0;
            const isLockActive = () => {
                if (status !== 'live') return false;
                if (endedLockedRef.current) return false;
                if (gracefulHideLatchedRef.current) return false;
                return true;
            };
            player.addEventListener('timeupdate', () => {
                // Track the current play position only when we're not actively
                // mid-seek and not paused. Mux's natural buffer-recovery seeks
                // briefly toggle player.seeking which we filter here.
                if (!player.paused && !player.seeking) {
                    lastValidTime = player.currentTime;
                }
            });
            player.addEventListener('pause', () => {
                if (!isLockActive()) return;
                // Don't fight Mux's natural end-of-stream pause
                if (player.ended) return;
                // Revert pause — call play(). Promise rejection is non-fatal
                // (e.g. autoplay policy block on a fresh element).
                player.play().catch(() => { /* defensive */ });
            });
            player.addEventListener('seeking', () => {
                if (!isLockActive()) return;
                // Filter Mux's internal seek events (live-edge corrections,
                // buffer recovery, segment boundary jumps) — only revert
                // significant user-initiated seeks. 5s threshold is a deliberate
                // safety margin: typical Mux internal seek deltas are <1s, with
                // worst-case live-edge corrections under 3s; deliberate user
                // scrubs are 10s+ (skip buttons or timeline drags). 5s sits
                // safely between the two regimes so we never fight Mux's own
                // recovery while still catching any user vector that slips past
                // the hidden UI (e.g. mobile double-tap-to-seek if it ever
                // surfaces in a future Mux Player release).
                const delta = Math.abs(player.currentTime - lastValidTime);
                if (delta < 5) return;
                // Revert to last known playing position. Property setter can
                // throw on iOS in rare lifecycle edges (element being torn down).
                try {
                    player.currentTime = lastValidTime;
                } catch (_) { /* defensive — silent fail keeps stream playing */ }
            });

            // ── Pre-recorded join-at-current-position (parity with live edge) ──
            // For pre-recorded classes during the airing window, a viewer who
            // joins late should land where the rest of the cohort is — the
            // equivalent of "live edge" that true-live classes get for free
            // from Mux's ll-live default. Without this seek, on-demand mode
            // defaults to t=0, so a viewer 6 minutes late on a 30-min class
            // would start at the beginning and have the player flip to ended
            // (via Workouts.js auto-transition at scheduledAt + duration)
            // 24 minutes in — losing the last 6 minutes of content and
            // experiencing a jarring mid-content cutoff. This effect makes
            // pre-recorded behave like live for late joiners.
            //
            // Why loadedmetadata: HTMLMediaElement requires media metadata to
            // be parsed before currentTime assignment is reliable across
            // browsers. loadedmetadata is the first event guaranteed to fire
            // with player.media.duration populated.
            //
            // Why update lastValidTime FIRST: the seeking listener above
            // reverts any seek with `Math.abs(currentTime - lastValidTime) > 5`
            // back to lastValidTime — by design, to defeat user scrubs through
            // any gesture surface that bypasses the hidden controls. A naive
            // programmatic seek to offsetSec while lastValidTime=0 would
            // trigger that revert and snap us back to t=0. Setting
            // lastValidTime to the target BEFORE assigning currentTime makes
            // the resulting seeking event compute delta=0 and no-op.
            //
            // Why { once: true }: a single seek per player lifetime. Silent
            // reloads / retries rebuild the player entirely (createPlayer is
            // called fresh), each rebuild registers its own listener, and
            // each seeks to the CURRENT real-time offset — matching live's
            // "always rejoin at live edge" behavior on rebuild.
            //
            // Bounds:
            //   - offset < 1s: viewer is essentially on time; no seek needed.
            //   - offset >= duration - 1s: viewer arrived past the natural
            //     end (rare — status would normally have flipped to 'ended'
            //     by then). Skip the seek and let the player fire `ended`
            //     naturally rather than seeking past the asset end.
            if (isPrerecorded && status === 'live' && scheduledAt) {
                const seekToJoinOffset = () => {
                    const startMs = new Date(scheduledAt).getTime();
                    if (!Number.isFinite(startMs)) return;
                    const offsetSec = Math.max(0, (serverNow() - startMs) / 1000);
                    if (offsetSec < 1) return;
                    const dur = player.media?.duration;
                    if (Number.isFinite(dur) && offsetSec >= dur - 1) return;
                    // Update lastValidTime BEFORE the seek so the seek-revert
                    // listener (declared above in this same closure) sees
                    // delta=0 and short-circuits.
                    lastValidTime = offsetSec;
                    try {
                        player.currentTime = offsetSec;
                    } catch (_) { /* defensive — silent fail keeps player at t=0 */ }
                };
                player.addEventListener('loadedmetadata', seekToJoinOffset, { once: true });
            }

            playerContainerRef.current.appendChild(player);
            playerElementRef.current = player;

            // ── Drift correction (premiere sync; pre-recorded airing only) ──
            // Keep this viewer locked to the cohort's wall-clock position. Every 15s,
            // if playback has drifted >3s from (serverNow − scheduledAt) — e.g. after a
            // buffer — nudge back so all viewers watch the same moment and end together.
            // Cooperates with the seek-revert lock by updating `lastValidTime` FIRST so
            // the resulting `seeking` event computes delta≈0 and no-ops. Stays away from
            // the tail so it never races the natural `ended`. No-op until the server
            // clock is synced (serverNow()===Date.now()) and metadata is finite.
            if (isPrerecorded && status === 'live' && scheduledAt) {
                const driftStartMs = new Date(scheduledAt).getTime();
                if (Number.isFinite(driftStartMs)) {
                    driftTimer = setInterval(() => {
                        if (cancelled) return;
                        if (endedLockedRef.current || gracefulHideLatchedRef.current) return;
                        if (player.paused || player.seeking) return;
                        const d = player.media?.duration;
                        const c = player.media?.currentTime;
                        if (!Number.isFinite(d) || !Number.isFinite(c)) return;
                        const target = (serverNow() - driftStartMs) / 1000;
                        if (target < 1 || target >= d - 1) return;   // never chase the tail
                        if (Math.abs(c - target) <= 3) return;        // within tolerance — leave it
                        lastValidTime = target;                       // cooperate with seek-revert lock
                        try { player.currentTime = target; } catch (_) { /* defensive */ }
                    }, 15000);
                }
            }

            // Safety net: if not playing after 15 seconds, retry
            // Pre-recorded assets are more reliable, but still use safety net
            safetyTimer = setTimeout(() => {
                if (cancelled) return;
                if (player.style.opacity === '0') {
                    if (retryCount < maxRetries) {
                        console.log(`Mux Player not playing after 15s — retrying (${retryCount + 1}/${maxRetries})`);
                        retryCount++;
                        createPlayer();
                    } else if (!hasPlayedEverRef.current) {
                        // Retries exhausted and still never played — schedule a silent
                        // reload instead of flashing an error. This is the common case
                        // during slow Mux HLS manifest propagation.
                        console.log('Mux Player safety net exhausted without playback — scheduling silent reload');
                        scheduleSilentReload();
                    }
                }
            }, 15000);
        }

        createPlayer();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (safetyTimer) clearTimeout(safetyTimer);
            if (driftTimer) clearInterval(driftTimer);
            if (playerContainerRef.current?.firstChild) {
                playerContainerRef.current.removeChild(playerContainerRef.current.firstChild);
            }
            playerElementRef.current = null;
        };
    }, [playerLoaded, playbackId, status, isPrerecorded, warmingUp, reloadKey, scheduleSilentReload, isAdmin]);

    // Reset silent-reload bookkeeping whenever the class leaves the live state — so
    // the next live session starts with a fresh cap and no pending timers. Also reset
    // the unmute preference: each live session is its own audio-consent moment (a
    // viewer who joins a class, ends it, then joins a different one should be asked
    // again rather than inheriting the prior session's unmuted state silently).
    useEffect(() => {
        if (status !== 'live') {
            silentReloadsRef.current = 0;
            if (silentReloadTimerRef.current) {
                clearTimeout(silentReloadTimerRef.current);
                silentReloadTimerRef.current = null;
            }
            unmutedRef.current = false;
            setNeedsUnmute(false);
        }
        // Reset the ended lock whenever status leaves 'live'. When it goes back to
        // 'live' (e.g. a fresh session), we want a clean slate — otherwise a viewer
        // who saw a class end and then joins a NEW live session in the same tab
        // would have their overlay stuck from the prior class.
        // Reset the graceful-hide latch + active state on the same condition — a
        // fresh class mount should start with the player visible and no overlay;
        // these only have meaning during the final 2s of a sealed manifest.
        if (status === 'scheduled') {
            endedLockedRef.current = false;
            gracefulHideLatchedRef.current = false;
            setGracefulHideActive(false);
        }
    }, [status]);

    // ── Auto-exit fullscreen when the class ends ──
    // The thank-you overlay sits at the LivePlayer container's z-index 10, OUTSIDE
    // the <mux-player> element. When a viewer enters fullscreen via Mux's built-in
    // button, only the player goes fullscreen — our overlay is a sibling so it's
    // invisible inside the fullscreen scope. Without this, the viewer sees the
    // player's last frame frozen until they manually exit fullscreen, blocking
    // them from seeing/using the favorite + comments UI.
    //
    // Triggers on the same conditions as the overlay render (line 926) so they
    // synchronize in the same React tick — no visible gap between fullscreen
    // exit and overlay appearance. Idempotent: exit is a no-op if already
    // exited; we silently catch any rejection (Safari is sometimes finicky
    // about exit timing during state transitions).
    useEffect(() => {
        const shouldExit = playerState === 'ended' || status === 'ended' || gracefulHideActive;
        if (!shouldExit) return;
        // Standard Fullscreen API (modern browsers + iOS 13+)
        try {
            if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
                document.exitFullscreen().catch(() => { /* Safari sometimes rejects mid-transition */ });
            } else if (document.webkitFullscreenElement && typeof document.webkitExitFullscreen === 'function') {
                document.webkitExitFullscreen();
            }
        } catch (_) { /* defensive — silent fail still leaves overlay rendered */ }
        // iOS Safari video-element-specific fullscreen API. Mux Player's internal
        // <video> may use this independently of document.fullscreenElement on
        // older iOS Safari versions (which don't yet implement the standard API
        // for video elements).
        try {
            const player = playerElementRef.current;
            const videoEl = player?.media || (player?.querySelector && player.querySelector('video'));
            if (videoEl && videoEl.webkitDisplayingFullscreen && typeof videoEl.webkitExitFullscreen === 'function') {
                videoEl.webkitExitFullscreen();
            }
        } catch (_) { /* iOS quirks — overlay still renders regardless */ }
    }, [playerState, status, gracefulHideActive]);

    // ── Auto-fullscreen on rotate to landscape (item 2, mobile/tablet only) ──
    // When a viewer rotates to landscape during an actively playing class,
    // automatically request fullscreen so they get the immersive experience
    // without having to manually tap the fullscreen button. iOS Safari MAY
    // reject the request if it doesn't consider the orientation change a
    // user gesture — that's a silent no-op (the user can still tap the
    // fullscreen button manually), NOT a regression. iOS 16+ handles this
    // more reliably; older versions may stay unfullscreened.
    //
    // Fires only ONCE per class session (tracked by hasAutoFullscreenedRef).
    // If the user manually exits fullscreen and rotates again, we respect
    // their choice. Ref resets when status leaves 'live' so a fresh class
    // session gets a clean attempt.
    //
    // Closure pattern: playerStateRef mirrors the React state into a ref so
    // the orientation handler reads live values without re-registering on
    // every state change. Same pattern the codebase uses for endingAtRef /
    // lifecycleRef (see CLAUDE.md "handler closures vs. live refs").
    const hasAutoFullscreenedRef = useRef(false);
    const playerStateRef = useRef(playerState);
    useEffect(() => { playerStateRef.current = playerState; }, [playerState]);
    useEffect(() => {
        if (status !== 'live') {
            // Reset the once-per-session flag for the next class
            hasAutoFullscreenedRef.current = false;
            return;
        }
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        // Only auto-fullscreen on touch devices — desktop has its own UX
        const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        if (!isTouchDevice) return;

        const orientationMql = window.matchMedia('(orientation: landscape)');
        const handleOrientation = (e) => {
            if (!e.matches) return;                                      // only on entry to landscape
            if (hasAutoFullscreenedRef.current) return;                  // once per session
            if (playerStateRef.current !== 'playing') return;            // class actively playing only
            if (document.visibilityState !== 'visible') return;          // tab in foreground
            if (document.fullscreenElement || document.webkitFullscreenElement) return;
            const player = playerElementRef.current;
            if (!player) return;
            // Mark BEFORE the request fires — if iOS silently rejects, we
            // still honor the once-per-session contract for subsequent
            // rotations (avoids the "user keeps rotating, we keep retrying" loop).
            hasAutoFullscreenedRef.current = true;
            try {
                if (typeof player.requestFullscreen === 'function') {
                    player.requestFullscreen().catch(() => { /* iOS may reject without gesture */ });
                } else if (typeof player.webkitRequestFullscreen === 'function') {
                    player.webkitRequestFullscreen();
                } else {
                    // Older iOS Safari: video element has its own fullscreen API
                    const videoEl = player.media || (player.querySelector && player.querySelector('video'));
                    if (videoEl && typeof videoEl.webkitEnterFullscreen === 'function') {
                        try { videoEl.webkitEnterFullscreen(); } catch (_) { /* no gesture */ }
                    }
                }
            } catch (_) { /* defensive — fullscreen button is always available as fallback */ }
        };

        // Standard listener (modern browsers); fall back to deprecated
        // addListener for older Safari versions.
        if (typeof orientationMql.addEventListener === 'function') {
            orientationMql.addEventListener('change', handleOrientation);
            return () => orientationMql.removeEventListener('change', handleOrientation);
        } else if (typeof orientationMql.addListener === 'function') {
            orientationMql.addListener(handleOrientation);
            return () => orientationMql.removeListener(handleOrientation);
        }
    }, [status]);

    // Countdown timer for pre-class state
    const [countdown, setCountdown] = useState('');
    useEffect(() => {
        if (status !== 'scheduled' || !scheduledAt) return;
        const update = () => {
            const now = new Date();
            const start = new Date(scheduledAt);
            const diff = Math.max(0, start - now);
            if (diff <= 0) { setCountdown('Starting soon...'); return; }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setCountdown(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [status, scheduledAt]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
            borderRadius: 20,
            overflow: 'hidden',
        }}>
            {/* Branded background — always visible at z-index 0 */}
            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                zIndex: 0,
            }}>
                <span style={{
                    fontFamily: 'var(--mom-font-serif)',
                    fontSize: 'clamp(18px, 4vw, 28px)',
                    fontWeight: 700, fontStyle: 'italic',
                    color: 'var(--mom-white)',
                    textAlign: 'center', lineHeight: 1.2,
                    opacity: 0.9,
                }}>moments of movement</span>
                <span style={{
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 'clamp(8px, 1.5vw, 11px)',
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.4)',
                    marginTop: 6,
                }}>with Danielle</span>
            </div>

            {/* Mux Player container */}
            {status === 'live' && (
                <div ref={playerContainerRef} style={{
                    position: 'absolute', inset: 0, zIndex: 1,
                }} />
            )}

            {/* Countdown overlay (scheduled state) — "You're In!" hero + countdown.
                The wordmark is replaced by a celebratory "You're In!" headline once the user
                has joined the waiting room, giving them immediate confirmation that they're
                checked in and connected. Countdown layout below is unchanged. */}
            {status === 'scheduled' && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 2,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                    color: '#fff', textAlign: 'center', padding: 32,
                }}>
                    <span style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(26px, 5vw, 40px)',
                        fontWeight: 700, fontStyle: 'italic',
                        color: 'var(--mom-white)',
                        lineHeight: 1.1, marginBottom: 40,
                    }}>You're In!</span>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                        letterSpacing: '0.2em', textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.5)', marginBottom: 8,
                    }}>Class starts in</p>
                    <p style={{
                        fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(32px, 6vw, 48px)',
                        fontWeight: 700, fontStyle: 'italic',
                        marginBottom: 8,
                    }}>{countdown}</p>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        color: 'rgba(255,255,255,0.6)',
                    }}>{title}</p>
                </div>
            )}

            {/* ── Ended overlay — branded "Thank you" screen ──
                Authoritative signal: Firestore `status === 'ended'` (admin tapped End Class).
                Fallback: playerState === 'ended' is ONLY meaningful for pre-recorded classes
                (on-demand asset finished playing). For live streams, playerState can never
                reach 'ended' — the `ended` event handler always silent-reloads instead. This
                is the load-bearing fix that prevents the thank-you overlay from latching
                mid-broadcast on a transient manifest hiccup. */}
            {/* Overlay trigger:
                  - `playerState === 'ended'` — Mux finished the asset (pre-recorded natural end,
                    or graceful-end promoted a live `ended` event, or the 25s safety timer elapsed).
                  - `status === 'ended' && !gracefullyEndingRef.current` — late-joiner path: status
                    already flipped (e.g. 120s admin cleanup caught up), and this viewer isn't in
                    graceful-ending mode (because they never played). Show overlay immediately.
                  - `gracefulHideActive` — zero-slate safety net: set by the timeupdate handler
                    the moment pre-hide fires (finite-duration manifest + final 2s + played-ever).
                    Covers the window between pre-hide and Mux `ended` firing so no slate/dance/
                    replay can ever leak through. Audio continues underneath; the viewer hears
                    Danielle's closing words as the card fades in. This is the load-bearing
                    "I do not want to see the Mux slate image pop up at the end for any reason"
                    guarantee — combined with the branded background at z-index 0, the player
                    at z-index 1 going opacity:0, and this overlay at z-index 10, there is no
                    z-layer where Mux's native UI can paint a visible pixel after pre-hide.
                  - Suppressed while `gracefullyEndingRef.current === true` AND neither of the
                    above paths has fired — mid-drain, before finite-duration has been observed. */}
            {(playerState === 'ended' || gracefulHideActive || (status === 'ended' && !gracefullyEndingRef.current)) && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 10,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                    color: '#fff', textAlign: 'center',
                    padding: 'clamp(16px, 4vw, 32px)',
                    gap: 'clamp(8px, 2vw, 16px)',
                    overflowY: 'auto',
                }}>
                    <p style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(18px, 4vw, 28px)',
                        fontStyle: 'italic', fontWeight: 700,
                        margin: 0,
                        color: 'var(--mom-white)',
                    }}>Thank you for joining!</p>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        color: 'rgba(255,255,255,0.5)',
                        lineHeight: 1.5, maxWidth: 320,
                        margin: 0,
                    }}>Great workout! The replay will be available soon.</p>
                    {onToggleFavorite && (
                        <button onClick={onToggleFavorite} style={{
                            background: isFavorited ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)',
                            border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 50,
                            padding: '10px 24px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                            transition: 'all 0.2s ease',
                        }}>
                            <svg width="18" height="18" viewBox="0 0 24 24"
                                fill={isFavorited ? 'var(--mom-primary)' : 'none'}
                                stroke={isFavorited ? 'var(--mom-primary)' : 'rgba(255,255,255,0.8)'}
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                style={{ transition: 'all 0.2s ease' }}>
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                            <span style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                                color: isFavorited ? 'var(--mom-primary)' : 'rgba(255,255,255,0.8)',
                            }}>{isFavorited ? 'Favorited!' : 'Enjoyed this class?'}</span>
                        </button>
                    )}
                </div>
            )}

            {/* "Starting class…" — branded overlay visible the moment lifecycle flips
                to live, until Mux Player fires `playing` (D-055). With ll-live this
                window is typically 1-3s, so the overlay intentionally feels like "the
                class is about to start" rather than "something is loading". Solid
                brand primary background (no gradient) differentiates it from terminal
                states. Suppressed once we've transitioned into any terminal state
                (ended/error/stalled). Pre-recorded skips it (on-demand playback
                starts effectively instantly). Drain window (isDraining) also skips —
                the player is already playing; no overlay needed. */}
            {status === 'live' && !isDraining && playerState !== 'playing' && playerState !== 'ended' && playerState !== 'error' && playerState !== 'stalled' && !isPrerecorded && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 3,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                    color: '#fff', textAlign: 'center', padding: 32,
                    pointerEvents: 'none',
                    transition: 'opacity 200ms ease',
                }}>
                    <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: '#fff',
                        animation: 'pulse 1.2s ease-in-out infinite',
                        marginBottom: 18,
                        opacity: 0.95,
                    }} />
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 18, fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: '#fff',
                    }}>Starting class…</span>
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 11, letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.7)',
                        marginTop: 10,
                    }}>with Danielle</span>
                </div>
            )}

            {/* Stalled state — shown when the stream never started (empty HLS manifest,
                broken broadcast upstream) or when all retries exhausted without playback.
                This is deliberately NOT the "Thank you for joining" overlay: the viewer
                never actually saw the class, so calling it ended would be misleading.
                "Try Again" bumps reloadKey, which rebuilds the mux-player element. */}
            {playerState === 'stalled' && status !== 'ended' && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 10,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
                    color: '#fff', textAlign: 'center', padding: 32,
                }}>
                    <span style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(18px, 4vw, 28px)',
                        fontWeight: 700, fontStyle: 'italic',
                        color: 'var(--mom-white)',
                        lineHeight: 1.2, marginBottom: 6, opacity: 0.9,
                    }}>moments of movement</span>
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 'clamp(8px, 1.5vw, 11px)',
                        letterSpacing: '0.15em', textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.4)',
                        marginBottom: 28,
                    }}>with Danielle</span>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 15,
                        color: 'rgba(255,255,255,0.85)',
                        marginBottom: 8, maxWidth: 340, lineHeight: 1.5,
                    }}>Having trouble reaching the live stream.</p>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                        color: 'rgba(255,255,255,0.5)',
                        marginBottom: 22, maxWidth: 300, lineHeight: 1.5,
                    }}>Give it a moment — if it doesn't come up, try again.</p>
                    <button onClick={() => {
                        // Manual retry — reset the silent-reload budget so the viewer gets
                        // a full new cycle of auto-retries after this tap (otherwise tapping
                        // Try Again at the cap would just re-render with 0 remaining retries).
                        silentReloadsRef.current = 0;
                        if (silentReloadTimerRef.current) {
                            clearTimeout(silentReloadTimerRef.current);
                            silentReloadTimerRef.current = null;
                        }
                        setReloadKey((k) => k + 1);
                    }} style={{
                        background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)',
                        color: '#fff', padding: '10px 24px', borderRadius: 50,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', letterSpacing: '0.04em',
                        transition: 'all 0.2s ease',
                    }}>Try Again</button>
                </div>
            )}

            {/* Error state */}
            {playerState === 'error' && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 2,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#fff', textAlign: 'center', padding: 32,
                }}>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 16,
                        marginBottom: 12,
                    }}>Unable to load the stream</p>
                    <button onClick={() => window.location.reload()} style={{
                        background: 'rgba(255,255,255,0.15)', border: 'none',
                        color: '#fff', padding: '10px 24px', borderRadius: 12,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        fontWeight: 600, cursor: 'pointer',
                    }}>Try Again</button>
                </div>
            )}

            {/* "Tap for sound" overlay — THE primary viewer gesture to unlock audio.
                Aligns with industry-standard web live video (Twitch, YouTube Live,
                Instagram Live on web): the stream auto-starts muted (iOS/mobile
                autoplay policy), the live feed is already visible behind a semi-
                transparent branded scrim, and one tap flips to unmuted playback.
                The viewer's gesture of intent happened earlier on the Workouts tile
                ("Join Waiting Room") — this is the single in-player touchpoint.
                Sits above the player (z-index 4) but below ended/stalled/error so
                terminal states always win. Shown for BOTH true-live and pre-recorded
                during their airing window — both mount muted (status === 'live'), so
                both need this single in-player gesture to unlock audio. (VideoPlayerModal,
                used for on-demand / post-class replay, does not render this component.) */}
            {needsUnmute && playerState === 'playing' && status === 'live' && (
                <button
                    onClick={() => {
                        const p = playerElementRef.current;
                        if (p) {
                            try {
                                p.muted = false;
                                // Some mobile browsers leave volume at 0 until set explicitly.
                                if (p.volume === 0) p.volume = 1;
                                p.removeAttribute('muted');
                            } catch (e) {
                                // Defensive: property set shouldn't throw on mux-player,
                                // but we never want a failed unmute attempt to crash the player.
                                console.warn('Unmute attempt failed', e);
                            }
                        }
                        unmutedRef.current = true;
                        setNeedsUnmute(false);
                    }}
                    style={{
                        position: 'absolute', inset: 0, zIndex: 4,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        border: 'none', color: '#fff',
                        cursor: 'pointer', padding: 32,
                        fontFamily: 'inherit',
                    }}
                    aria-label="Tap for sound"
                >
                    <div style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'var(--mom-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 12px 36px rgba(0,0,0,0.35), 0 0 0 4px rgba(255,255,255,0.18)',
                        marginBottom: 18,
                    }}>
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none"
                            stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 5 6 9H2v6h4l5 4V5z" />
                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                        </svg>
                    </div>
                    <span style={{
                        fontFamily: 'var(--mom-font-serif)',
                        fontSize: 'clamp(20px, 4vw, 26px)',
                        fontStyle: 'italic', fontWeight: 700,
                        color: '#fff', lineHeight: 1.2, marginBottom: 6,
                        textShadow: '0 2px 10px rgba(0,0,0,0.45)',
                    }}>Tap for sound</span>
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 12, letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.75)',
                        textShadow: '0 1px 6px rgba(0,0,0,0.35)',
                    }}>You're live with Danielle</span>
                </button>
            )}

            {/* Live reactions — glass pill at bottom-RIGHT, lifted ABOVE the Mux control bar.
                `bottom: 90` clears Mux Player's fullscreen button (which lives at the
                bottom-right corner of the control row). Sitting above the controls means
                reactions never obscure any player UI — in normal OR fullscreen mode.
                Floating emojis rise from this same anchor so buttons and animation stay
                spatially coupled.
                Floating emojis render for EVERYONE during a live class (admin observes
                too — they need to feel the energy in the room). Tap buttons are viewer-only:
                admin sees reactions but doesn't send them.
                Both true-live and pre-recorded participate (shared-moment parity).

                Hidden the moment playerState hits 'ended' (Mux finished the asset or the
                graceful-end path promoted an `ended` event) — otherwise the reactions
                overlay would remain visible underneath the "Thank you for joining!" card
                during the gap between the stream ending and the 120s admin status flip,
                which looks broken. */}
            {status === 'live' && playerState !== 'ended' && (
                <div style={{
                    position: 'absolute', bottom: 90, right: 12, zIndex: 12,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                    pointerEvents: 'none', // buttons opt back in below; let player controls receive other clicks
                }}>
                    {/* Floating emojis — rise and fade above buttons */}
                    <div style={{
                        position: 'absolute', bottom: '100%', right: 0,
                        width: 100, height: 160, pointerEvents: 'none',
                    }}>
                        {floatingEmojis.map((f) => (
                            <FloatingEmojiInline key={f.id} id={f.id} emoji={f.emoji} onComplete={removeFloatingEmoji} />
                        ))}
                    </div>
                    {/* Reaction buttons — viewers only, not admin.
                        Stacked vertically, filtered to heart + fire only. Sits at bottom: 90
                        to clear Mux's fullscreen button (bottom-right of control row). */}
                    {!isAdmin && userId && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', gap: 6,
                            padding: '6px 4px', borderRadius: 22,
                            background: 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            pointerEvents: 'auto',
                        }}>
                            {REACTION_EMOJIS.filter((r) => r.key === 'heart' || r.key === 'fire').map((r) => (
                                <button
                                    key={r.key}
                                    onClick={() => handleReactionTap(r)}
                                    style={{
                                        width: 36, height: 36,
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.12)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 16,
                                        transition: 'transform 0.15s ease, background 0.15s ease',
                                    }}
                                    onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(1.25)'; e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
                                    onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                                    title={r.label}
                                >
                                    {r.emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Note: "Also in class" presence row lives below the player (rendered by Workouts.js)
                so it has room for avatar + first-name-last-initial chips on both mobile and desktop. */}
        </div>
    );
}

// Inline floating emoji — rises and fades within the player container.
// Positioned with `right` to match the reaction pill's bottom-right location,
// so emojis appear to emit from the buttons and drift up-and-over with a subtle
// horizontal wobble.
function FloatingEmojiInline({ emoji, id, onComplete }) {
    const [style, setStyle] = useState({
        position: 'absolute',
        bottom: 0,
        right: Math.random() * 40 + 10,
        fontSize: 22,
        opacity: 1,
        transform: 'scale(1)',
        transition: 'all 2s ease-out',
        pointerEvents: 'none',
        zIndex: 20,
    });

    useEffect(() => {
        requestAnimationFrame(() => {
            setStyle((prev) => ({
                ...prev,
                bottom: 80 + Math.random() * 60,
                right: prev.right + (Math.random() - 0.5) * 30,
                opacity: 0,
                transform: 'scale(0.6)',
            }));
        });
        const timer = setTimeout(() => onComplete(id), 2100);
        return () => clearTimeout(timer);
    }, [id, onComplete]);

    return <span style={style}>{emoji}</span>;
}

export default LivePlayer;
